import { defineMessage, useIntl } from "react-intl";

import ChartTrendUpIcon from "@atlaskit/icon/core/chart-trend-up";
import DashboardIcon from "@atlaskit/icon/core/dashboard";
import DataFlowIcon from "@atlaskit/icon/core/data-flow";
import DepartmentIcon from "@atlaskit/icon/core/department";
import FieldCheckboxGroupIcon from "@atlaskit/icon/core/field-checkbox-group";
import LightbulbIcon from "@atlaskit/icon/core/lightbulb";
import PageIcon from "@atlaskit/icon/core/page";
import PagesIcon from "@atlaskit/icon/core/pages";
import PersonAddIcon from "@atlaskit/icon/core/person-add";
import PremiumIcon from "@atlaskit/icon/core/premium";
import TeamsIcon from "@atlaskit/icon/core/teams";
import PageLiveDocIcon from "@atlaskit/icon-lab/core/page-live-doc";

import { useAdg4TabsContext } from "@/components/adg4/tabs/src/tabs";
import { Link } from "@/libraries/design/components";
import { useLocalizedHref } from "@/libraries/web-framework/src/localization/use-localized-href";
import { DataEventProps } from "@/libraries/wpl/foundations/src/data-event-props";
import { ContentTypeVideo } from "@/templates/software/confluence/confluence-software-page/src/sections/content-types/content-type-video";
import { TabProps } from "@/templates/software/confluence/confluence-software-page/src/sections/content-types/tab-type";

const getCtaDataEventProps = (
    contentType: string,
    defaultMessage: string,
    formattedCtaText: string
): DataEventProps => {
    return {
        "data-product-key": "confluence",
        "data-event-component": "linkButton",
        "data-event-container": "contentTypesSection",
        "data-event": "clicked",
        "data-label": formattedCtaText.toLowerCase().replace(" ", "-"),
        "data-label-english": defaultMessage,
        "data-track-event": `confluence-pt.content-types.${contentType}.cta`
    } as DataEventProps;
};

export const PagesContentType = ({ index }: TabProps) => {
    const { isCurrentTab } = useAdg4TabsContext();
    const intl = useIntl();

    const pagesCtaText = defineMessage({
        id: "HomePageTemplateV2.confluenceCrossflowHero.pagesCtaButton",
        defaultMessage: "Explore pages"
    });

    const formattedPagesCtaText = intl.formatMessage(pagesCtaText);

    const pagesCtaLink = useLocalizedHref("/software/confluence/pages");

    return (
        <ContentTypeVideo
            active={isCurrentTab(index)}
            title={"Get everyone on the same page"}
            description={
                "Take notes, plan projects, and refine ideas with flexible Confluence live docs and pages. Or let AI do it for you."
            }
            iconCollection={[
                {
                    description: "Brainstorming templates",
                    icon: <PagesIcon label="" spacing="spacious" />
                },
                {
                    description: "Real-time collaboration with live docs",
                    icon: <PageLiveDocIcon label="" spacing="spacious" />
                },
                {
                    description: "AI editor to fill in the blanks",
                    icon: <PremiumIcon label="" spacing="spacious" />
                }
            ]}
            cta={
                <div>
                    {/* eslint-disable-next-line @atlassian/a11y/anchor-has-content, @atlassian/a11y/link-name */}
                    <Link
                        href={pagesCtaLink}
                        text={formattedPagesCtaText}
                        type={"primary"}
                        dataEventProps={getCtaDataEventProps(
                            "pages",
                            pagesCtaText.defaultMessage,
                            formattedPagesCtaText
                        )}
                    />
                </div>
            }
            videoSrcMp4="https://dam-cdn.atl.orangelogic.com/AssetLink/mp73h78mrau2cl0p7jc381iml8cgd2iy.mp4"
            videoSrcWebm="https://dam-cdn.atl.orangelogic.com/AssetLink/8842i0ee1unjy3355235q4bb838pnv40.webm"
            videoPreviewSrc="https://dam-cdn.atl.orangelogic.com/AssetLink/6i87746a6ini3oyk1hxql81x7yq10ew6.webp"
            showBackground={true}
            useSpecificBackgroundStyle="purple"
            isUseReusableXflowFeatureInfo={true}
            hideSeparator={true}
            addPaddingAboveList={true}
        />
    );
};

export const WhiteboardsContentType = ({ index }: TabProps) => {
    const { isCurrentTab } = useAdg4TabsContext();
    const intl = useIntl();

    const whiteboardsCtaText = defineMessage({
        id: "HomePageTemplateV2.confluenceCrossflowHero.whiteboardsCtaButton",
        defaultMessage: "Explore whiteboards"
    });

    const formattedWhiteboardsCtaText = intl.formatMessage(whiteboardsCtaText);

    const ctaLink = useLocalizedHref("/software/confluence/whiteboards");

    return (
        <ContentTypeVideo
            active={isCurrentTab(index)}
            title={"Next-gen brainstorming"}
            description={
                "Capture free-flowing thoughts and collaborate on ideas with Confluence Whiteboards."
            }
            iconCollection={[
                {
                    description: "Countless templates to get started fast",
                    icon: <PageIcon label="" spacing="spacious" />
                },
                {
                    description: "Diagrams and flow charts",
                    icon: <ChartTrendUpIcon label="" spacing="spacious" />
                },
                {
                    description: "Team retrospectives",
                    icon: <TeamsIcon label="" spacing="spacious" />
                }
            ]}
            cta={
                <div>
                    {/* eslint-disable-next-line @atlassian/a11y/anchor-has-content, @atlassian/a11y/link-name */}
                    <Link
                        href={ctaLink}
                        text={formattedWhiteboardsCtaText}
                        type={"primary"}
                        dataEventProps={getCtaDataEventProps(
                            "whiteboards",
                            whiteboardsCtaText.defaultMessage,
                            formattedWhiteboardsCtaText
                        )}
                    />
                </div>
            }
            videoSrcMp4="https://dam-cdn.atl.orangelogic.com/AssetLink/77d2gcld0682t52faah0nqs5u52r5bv5.mp4"
            videoSrcWebm="https://dam-cdn.atl.orangelogic.com/AssetLink/a16ohebx20rk4821sq27884jymn32uw1.webm"
            videoPreviewSrc="https://dam-cdn.atl.orangelogic.com/AssetLink/02rq48pu8edm8x711iva8pervsq4b726.webp"
            showBackground={true}
            useSpecificBackgroundStyle="green"
            isUseReusableXflowFeatureInfo={true}
            hideSeparator={true}
            addPaddingAboveList={true}
        />
    );
};

export const DatabasesContentType = ({ index }: TabProps) => {
    const { isCurrentTab } = useAdg4TabsContext();
    const intl = useIntl();

    const databasesCtaText = defineMessage({
        id: "HomePageTemplateV2.confluenceCrossflowHero.databasesCtaButton",
        defaultMessage: "Explore databases"
    });

    const formattedDatabasesCtaText = intl.formatMessage(databasesCtaText);

    const ctaLink = useLocalizedHref("/software/confluence/databases");

    return (
        <ContentTypeVideo
            active={isCurrentTab(index)}
            title={"A new way to stay in sync"}
            description={
                "Add structure, sync in real-time, and visualize your team’s work with Confluence Databases."
            }
            iconCollection={[
                {
                    description: "Dynamic fields",
                    icon: <FieldCheckboxGroupIcon label="" spacing="spacious" />
                },
                {
                    description: "Configurable views",
                    icon: <DashboardIcon label="" spacing="spacious" />
                },
                {
                    description: "Aggregated data sources",
                    icon: <DataFlowIcon label="" spacing="spacious" />
                }
            ]}
            cta={
                <div>
                    {/* eslint-disable-next-line @atlassian/a11y/anchor-has-content, @atlassian/a11y/link-name */}
                    <Link
                        href={ctaLink}
                        text={formattedDatabasesCtaText}
                        type={"primary"}
                        dataEventProps={getCtaDataEventProps(
                            "databases",
                            databasesCtaText.defaultMessage,
                            formattedDatabasesCtaText
                        )}
                    />
                </div>
            }
            videoSrcMp4="https://wac-cdn.atlassian.com/misc-assets/videos/confluence-product-tour/PT_CT_Databases_LowBR_reencode.mp4"
            videoSrcWebm="https://wac-cdn.atlassian.com/misc-assets/videos/confluence-product-tour/PT_CT_Databases_LowBR.webm"
            videoPreviewSrc="https://wac-cdn.atlassian.com/misc-assets/webp-images/confluence/PT_CT_Databases_Preview.webp"
            showBackground={true}
            useSpecificBackgroundStyle="orange"
            isUseReusableXflowFeatureInfo={true}
            hideSeparator={true}
            addPaddingAboveList={true}
        />
    );
};

export const SpacesContentType = ({ index }: TabProps) => {
    const { isCurrentTab } = useAdg4TabsContext();
    const intl = useIntl();

    const spacesCtaText = defineMessage({
        id: "HomePageTemplateV2.confluenceCrossflowHero.spacesCtaButton",
        defaultMessage: "Explore knowledge sharing"
    });

    const formattedSpacesCtaText = intl.formatMessage(spacesCtaText);

    const ctaLink = useLocalizedHref("/software/confluence/spaces");

    return (
        <ContentTypeVideo
            active={isCurrentTab(index)}
            title={"Find what you need"}
            description={
                "Organize knowledge across teams, projects, and goals with Spaces in Confluence."
            }
            iconCollection={[
                {
                    description: "Structured page tree",
                    icon: <DepartmentIcon label="" spacing="spacious" />
                },
                {
                    description: "Centralized access to knowledge",
                    icon: <LightbulbIcon label="" spacing="spacious" />
                },
                {
                    description: "Customizable permissions",
                    icon: <PersonAddIcon label="" spacing="spacious" />
                }
            ]}
            cta={
                <div>
                    {/* eslint-disable-next-line @atlassian/a11y/anchor-has-content, @atlassian/a11y/link-name */}
                    <Link
                        href={ctaLink}
                        text={formattedSpacesCtaText}
                        type={"primary"}
                        dataEventProps={getCtaDataEventProps(
                            "spaces",
                            spacesCtaText.defaultMessage,
                            formattedSpacesCtaText
                        )}
                    />
                </div>
            }
            videoSrcMp4="https://dam-cdn.atl.orangelogic.com/AssetLink/336lite38u32861f1m4a00tt0yw543w3.mp4"
            videoSrcWebm="https://dam-cdn.atl.orangelogic.com/AssetLink/74a2y7d1bv6n64nh77m0x2t5c22324u0.webm"
            videoPreviewSrc="https://dam-cdn.atl.orangelogic.com/AssetLink/02s15d6mx7653268b02ow84vr1uug611.webp"
            showBackground={true}
            useSpecificBackgroundStyle="green"
            isUseReusableXflowFeatureInfo={true}
            hideSeparator={true}
            addPaddingAboveList={true}
        />
    );
};
